import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, Clock, Award, ThumbsUp } from "lucide-react";
import { cn } from "@/lib/utils";

export interface Provider {
  id: string;
  name: string;
  specialty: string;
  experience: string;
  rating: number;
  matchScore: number; // Relevance to patient's symptoms (0-100)
  imageUrl?: string;
  availableDates?: string[]; // ISO date strings
  nextAvailable?: string;
}

interface ProviderCardProps {
  provider: Provider;
  isSelected: boolean;
  onSelect: (provider: Provider) => void;
  showMatchScore?: boolean;
  className?: string;
}

export function ProviderCard({
  provider,
  isSelected,
  onSelect,
  showMatchScore = true,
  className,
}: ProviderCardProps) {
  // Function to render stars based on rating
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star
          key={`full-${i}`}
          className="w-4 h-4 fill-yellow-400 text-yellow-400"
        />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <div key="half" className="relative">
          <Star className="w-4 h-4 text-gray-300" />
          <div className="absolute top-0 left-0 w-1/2 overflow-hidden">
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
          </div>
        </div>
      );
    }

    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Star key={`empty-${i}`} className="w-4 h-4 text-gray-300" />
      );
    }

    return stars;
  };

  // Function to determine match score badge color
  const getMatchScoreBadgeColor = (score: number) => {
    if (score >= 90) return "bg-green-100 text-green-800 border-green-200";
    if (score >= 70) return "bg-blue-100 text-blue-800 border-blue-200";
    return "bg-gray-100 text-gray-800 border-gray-200";
  };

  return (
    <Card
      className={cn(
        "transition-all duration-200 cursor-pointer hover:shadow-md",
        isSelected
          ? "border-2 border-blue-500 shadow-md bg-blue-50"
          : "border border-gray-200 hover:border-blue-300",
        className
      )}
      onClick={() => onSelect(provider)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {provider.imageUrl ? (
            <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
              <img
                src={provider.imageUrl}
                alt={provider.name}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <span className="text-blue-600 font-semibold text-lg">
                {provider.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")}
              </span>
            </div>
          )}

          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">{provider.name}</h3>
                <p className="text-sm text-blue-600">{provider.specialty}</p>
              </div>
              {isSelected && (
                <Badge className="bg-blue-500">Selected</Badge>
              )}
            </div>

            <div className="mt-2 flex items-center gap-1">
              {renderStars(provider.rating)}
              <span className="text-xs text-gray-600 ml-1">
                ({provider.rating.toFixed(1)})
              </span>
            </div>

            <div className="mt-2 flex flex-wrap gap-2">
              <div className="flex items-center gap-1 text-xs text-gray-600">
                <Award className="w-3 h-3" />
                <span>{provider.experience}</span>
              </div>
              {provider.nextAvailable && (
                <div className="flex items-center gap-1 text-xs text-gray-600">
                  <Clock className="w-3 h-3" />
                  <span>Next: {provider.nextAvailable}</span>
                </div>
              )}
              {showMatchScore && (
                <Badge
                  className={cn(
                    "text-xs font-normal",
                    getMatchScoreBadgeColor(provider.matchScore)
                  )}
                  variant="outline"
                >
                  <ThumbsUp className="w-3 h-3 mr-1" />
                  {provider.matchScore}% Match
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
